import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MessageCircle, MapPin, Car, Play, Clock, Sun, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useLang, pick } from "@/contexts/LanguageContext";
import { pathFor, ROUTE_KEYS, carDetailPath, routeDetailPath } from "@/i18n/routeMap";
import { openWhatsApp } from "@/lib/whatsapp";
import SeoHead from "@/components/SeoHead";
import ServiceAreas from "@/components/ServiceAreas";
import { usePublishSlugMap } from "@/contexts/PageMetaContext";
import {
  buildLocalBusinessJsonLd,
  buildBreadcrumb,
  buildFaqJsonLd,
  buildRouteJsonLd,
  defaultRouteSeoTitle,
  defaultRouteSeoDescription,
} from "@/lib/seo";

const INTERNAL_LINKS = {
  en: { catalog: "View available luxury cars", contact: "Book by WhatsApp", more: "Explore more Costa Blanca routes", card: "Route" },
  es: { catalog: "Ver coches de lujo disponibles", contact: "Reservar por WhatsApp", more: "Explorar más rutas en Costa Blanca", card: "Ruta" },
  ru: { catalog: "Смотреть доступные премиальные автомобили", contact: "Забронировать через WhatsApp", more: "Смотреть другие маршруты Коста-Бланки", card: "Маршрут" },
  fr: { catalog: "Voir les voitures de luxe disponibles", contact: "Réserver via WhatsApp", more: "Découvrir d’autres itinéraires sur la Costa Blanca", card: "Itinéraire" },
  de: { catalog: "Verfügbare Luxusfahrzeuge ansehen", contact: "Über WhatsApp buchen", more: "Weitere Routen an der Costa Blanca entdecken", card: "Route" },
  it: { catalog: "Vedi le auto di lusso disponibili", contact: "Prenota via WhatsApp", more: "Scopri altri itinerari sulla Costa Blanca", card: "Itinerario" },
  nl: { catalog: "Bekijk beschikbare luxe auto’s", contact: "Boek via WhatsApp", more: "Ontdek meer routes aan de Costa Blanca", card: "Route" },
  pl: { catalog: "Zobacz dostępne luksusowe samochody", contact: "Zarezerwuj przez WhatsApp", more: "Odkryj więcej tras na Costa Blanca", card: "Trasa" },
};

export default function RouteDetail() {
  const { lang, t } = useLang();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [route, setRoute] = useState(null);
  const [allRoutes, setAllRoutes] = useState([]);
  const [settings, setSettings] = useState({});
  const [notFound, setNotFound] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    setRoute(null);
    setNotFound(false);
    (async () => {
      try {
        const [{ data }, s, all] = await Promise.all([
          api.get(`/routes/by-slug/${slug}`),
          api.get("/settings"),
          api.get("/routes"),
        ]);
        setRoute(data);
        setSettings(s.data || {});
        setAllRoutes((all.data || []).filter((r) => r.id !== data.id).slice(0, 3));
      } catch {
        setNotFound(true);
      }
    })();
  }, [slug]);

  usePublishSlugMap(route?.slug || null);

  if (notFound) {
    return (
      <div className="bg-[#0C0C0C] pt-32 pb-24 min-h-screen" data-testid="route-not-found">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h1 className="font-display text-[#F5F1EB] text-3xl">Route not found</h1>
          <button onClick={() => navigate(pathFor(ROUTE_KEYS.ROUTES, lang))} className="btn-gold mt-8" data-testid="back-to-routes">
            {t.routes.backToRoutes}
          </button>
        </div>
      </div>
    );
  }

  if (!route) return <div className="bg-[#0C0C0C] min-h-screen pt-40 text-center text-[#8A8A8A]">Loading…</div>;

  const title = pick(route.h1, lang) || pick(route.title, lang);
  const desc = pick(route.shortDescription, lang);
  const stops = route.stops?.[lang] || route.stops?.en || [];
  const recommended = pick(route.recommendedCarType, lang);
  const bestTime = pick(route.bestTime, lang);
  const duration = pick(route.durationText, lang);
  const whyPremium = pick(route.whyPremium, lang);
  const seoText = pick(route.seoText, lang);
  const ctaText = pick(route.ctaText, lang) || t.routes.book;

  const seoTitle = pick(route.seoTitle, lang) || defaultRouteSeoTitle(route, lang);
  const seoDesc = pick(route.metaDescription, lang) || defaultRouteSeoDescription(route, lang);

  // routeMedia (new) with legacy `images` fallback
  let media = (route.routeMedia || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  if (media.length === 0 && Array.isArray(route.images)) {
    media = route.images.map((u, i) => ({ type: "image", url: typeof u === "string" ? u : u.url, isMain: i === 0, order: i, posterUrl: "" }));
  }
  const heroItem = media.find((m) => m.isMain) || media[0];
  const gallery = media.filter((m) => m !== heroItem).slice(0, 6);
  const heroUrl = heroItem?.url || "";

  const breadcrumb = [
    { name: t.nav.routes, path: pathFor(ROUTE_KEYS.ROUTES, lang) },
    { name: title, path: routeDetailPath(slug, lang) },
  ];

  const jsonLd = [
    buildLocalBusinessJsonLd(settings),
    buildRouteJsonLd(route, lang),
    buildBreadcrumb(breadcrumb),
    buildFaqJsonLd(route.faqs || [], lang),
  ];

  const il = INTERNAL_LINKS[lang] || INTERNAL_LINKS.en;
  const faqsForLang = (route.faqs || []).filter((f) => pick(f.q, lang) && pick(f.a, lang));

  return (
    <div className="bg-[#0C0C0C]" data-testid="route-detail-page">
      <SeoHead
        title={seoTitle}
        description={seoDesc}
        image={heroUrl}
        lang={lang}
        pageKey={ROUTE_KEYS.ROUTE_DETAIL}
        slug={slug}
        slugMap={route.slug}
        noindex={route.noindex || route.seoEnabled === false || !route.active}
        readyLocales={Object.entries(route.localeStatus || {}).filter(([, status]) => status === "READY").map(([locale]) => locale)}
        jsonLd={jsonLd}
      />

      <section className="relative h-[60vh] md:h-[80vh] overflow-hidden">
        {heroItem?.type === "video" ? (
          <video src={heroItem.url} poster={heroItem.posterUrl} autoPlay muted loop playsInline className="w-full h-full object-cover" />
        ) : (
          <img src={heroItem?.url} alt={title} className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0C0C0C]/30 via-[#0C0C0C]/50 to-[#0C0C0C]" />
        <div className="absolute inset-0 flex flex-col justify-end pb-12 md:pb-20">
          <div className="max-w-7xl mx-auto px-6 md:px-10 w-full">
            <nav className="text-[11px] tracking-[0.18em] uppercase text-[#F5F1EB]/70 flex items-center gap-2 mb-4" data-testid="route-breadcrumb">
              <Link to={pathFor(ROUTE_KEYS.HOME, lang)} className="hover:text-[#C9A55A]">{t.nav.home || "Home"}</Link>
              <ChevronRight size={12} />
              <Link to={pathFor(ROUTE_KEYS.ROUTES, lang)} className="hover:text-[#C9A55A]">{t.nav.routes}</Link>
              <ChevronRight size={12} />
              <span className="text-[#C9A55A]">{title}</span>
            </nav>
            <motion.h1
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
              className="font-display text-[#F5F1EB] text-3xl sm:text-5xl md:text-6xl max-w-4xl leading-tight"
              data-testid="route-title">{title}</motion.h1>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-10">
          <div className="md:col-span-2 space-y-12">
            <div>
              <p className="text-lg text-[#F5F1EB]/80 font-light leading-relaxed" data-testid="route-description">{desc}</p>
            </div>

            {whyPremium && (
              <div data-testid="route-why-premium">
                <div className="text-overline text-[#C9A55A] mb-3">{t.routes.whyPremium}</div>
                <p className="text-[#F5F1EB]/75 font-light leading-relaxed">{whyPremium}</p>
              </div>
            )}

            {stops.length > 0 && (
              <div data-testid="route-stops-block">
                <div className="text-overline text-[#C9A55A] mb-5">{t.routes.stops}</div>
                <ol className="space-y-3">
                  {stops.map((s, i) => (
                    <li key={i} className="flex items-start gap-4 border-b border-[#2A2A2A] pb-3" data-testid={`route-stop-${i}`}>
                      <span className="font-display text-[#C9A55A] text-2xl shrink-0 w-10">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-[#F5F1EB] pt-1">{s}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {gallery.length > 0 && (
              <div data-testid="route-gallery">
                <div className="text-overline text-[#C9A55A] mb-5">{t.routes.gallery}</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {gallery.map((m, i) => (
                    <button key={i} onClick={() => m.type === "video" && setActiveVideo(m)}
                      className="relative aspect-[4/3] overflow-hidden bg-[#1a1a1a] group" data-testid={`route-media-${i}`}>
                      {m.type === "video" ? (
                        <>
                          <img src={m.posterUrl || heroItem?.url} alt="" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-[#0C0C0C]/40 flex items-center justify-center">
                            <Play className="text-[#C9A55A]" size={28} />
                          </div>
                        </>
                      ) : (
                        <img src={m.url} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {faqsForLang.length > 0 && (
              <div data-testid="route-faq">
                <div className="text-overline text-[#C9A55A] mb-4">FAQ</div>
                <div className="space-y-3">
                  {faqsForLang.map((f, i) => (
                    <details key={i} className="border border-[#2A2A2A] p-4 group" data-testid={`route-faq-${i}`}>
                      <summary className="cursor-pointer text-[#C9A55A] text-sm tracking-wide font-semibold list-none flex items-center justify-between">
                        <span>{pick(f.q, lang)}</span>
                        <ChevronRight size={14} className="transition-transform group-open:rotate-90" />
                      </summary>
                      <p className="mt-3 text-[#F5F1EB]/80 text-sm font-light leading-relaxed">{pick(f.a, lang)}</p>
                    </details>
                  ))}
                </div>
              </div>
            )}

            {seoText && <p className="text-sm text-[#8A8A8A] font-light leading-relaxed">{seoText}</p>}
          </div>

          <aside className="space-y-5" data-testid="route-aside">
            <div className="border border-[#2A2A2A] p-5">
              <div className="flex items-center gap-2 text-overline text-[#C9A55A] mb-2"><Car size={14} /> {t.routes.recommendedCar}</div>
              <p className="text-[#F5F1EB] text-sm">{recommended || "—"}</p>
            </div>
            {bestTime && (
              <div className="border border-[#2A2A2A] p-5">
                <div className="flex items-center gap-2 text-overline text-[#C9A55A] mb-2"><Sun size={14} /> {t.routes.bestTime}</div>
                <p className="text-[#F5F1EB] text-sm">{bestTime}</p>
              </div>
            )}
            {duration && (
              <div className="border border-[#2A2A2A] p-5">
                <div className="flex items-center gap-2 text-overline text-[#C9A55A] mb-2"><Clock size={14} /> {t.routes.duration}</div>
                <p className="text-[#F5F1EB] text-sm">{duration}</p>
              </div>
            )}
            <div className="border border-[#2A2A2A] p-5">
              <div className="flex items-center gap-2 text-overline text-[#C9A55A] mb-2"><MapPin size={14} /> Region</div>
              <p className="text-[#F5F1EB] text-sm">Costa Blanca, Alicante</p>
            </div>

            <button onClick={() => openWhatsApp(t.whatsapp.routeInquiry(title), settings, lang)} className="btn-red w-full" data-testid="route-book-whatsapp">
              <MessageCircle size={16} /> {ctaText}
            </button>

            <div className="border border-[#2A2A2A] p-5 space-y-3" data-testid="route-internal-links">
              <Link to={pathFor(ROUTE_KEYS.CATALOG, lang)} className="block text-sm text-[#C9A55A] hover:text-[#F5F1EB] tracking-wide" data-testid="route-link-catalog">
                → {il.catalog}
              </Link>
              <Link to={pathFor(ROUTE_KEYS.CONTACT, lang)} className="block text-sm text-[#C9A55A] hover:text-[#F5F1EB] tracking-wide" data-testid="route-link-contact">
                → {il.contact}
              </Link>
              <Link to={pathFor(ROUTE_KEYS.ROUTES, lang)} className="block text-sm text-[#C9A55A] hover:text-[#F5F1EB] tracking-wide" data-testid="route-link-more">
                → {il.more}
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {allRoutes.length > 0 && (
        <section className="border-t border-[#2A2A2A] py-14 px-6 md:px-10" data-testid="route-related">
          <div className="max-w-7xl mx-auto">
            <div className="text-overline text-[#C9A55A] mb-6">{il.more}</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {allRoutes.map((r) => {
                const rSlug = r.slug?.[lang] || r.slug?.en;
                if (!rSlug) return null;
                const rTitle = pick(r.title, lang);
                const rMedia = (r.routeMedia || [])[0]?.url;
                return (
                  <Link key={r.id} to={routeDetailPath(rSlug, lang)} className="group border border-[#2A2A2A] hover:border-[#C9A55A] transition-colors block" data-testid={`route-related-${rSlug}`}>
                    {rMedia && (
                      <div className="aspect-[4/3] overflow-hidden bg-[#1a1a1a]">
                        <img src={rMedia} alt={rTitle} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="text-overline text-[#C9A55A] mb-1">{il.card}</div>
                      <div className="text-[#F5F1EB] text-sm font-semibold">{rTitle}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <ServiceAreas lang={lang} areas={route.serviceAreas} />

      {activeVideo && (
        <div className="fixed inset-0 bg-[#0C0C0C]/95 z-50 flex items-center justify-center p-6" onClick={() => setActiveVideo(null)}>
          <video src={activeVideo.url} poster={activeVideo.posterUrl} controls autoPlay className="max-w-full max-h-full" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

// Avoid the unused-import lint; carDetailPath is reserved for future linking
void carDetailPath;
